import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SharedHeader from './SharedHeader';
import Footer from './Footer';
import {
  ChevronDown,
  ChevronUp,
  QrCode,
  Smartphone,
  Wifi,
  Mail,
  Phone,
  MessageSquare,
  Facebook,
  Twitter,
  Type,
  Palette,
  Settings,
  Download,
  History,
  HelpCircle,
  Shield,
  Zap,
  Globe
} from 'lucide-react';

const Help = () => {
  const { language } = useLanguage();
  const [openSections, setOpenSections] = useState<number[]>([0]); // 默认展开第一个section

  const toggleSection = (index: number) => {
    setOpenSections(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const content = {
    en: {
      title: 'Help & Documentation',
      subtitle: 'Everything you need to know about using QRCodeForge',
      sections: [
        {
          title: 'Getting Started',
          icon: <QrCode className="w-5 h-5" />,
          content: [
            {
              question: 'How do I create my first QR code?',
              answer: [
                '1. Choose your QR code type from the left panel (URL, Text, Email, etc.)',
                '2. Fill in the required information in the form',
                '3. Customize colors and size if desired',
                '4. Your QR code will generate automatically',
                '5. Right-click and save or use the download options'
              ]
            },
            {
              question: 'What types of QR codes can I create?',
              answer: [
                '• **URL/Website**: Link to any website or webpage',
                '• **Plain Text**: Any text content up to 4,296 characters',
                '• **Email**: Pre-filled email with recipient, subject, and message',
                '• **Phone**: Direct dial phone numbers',
                '• **SMS**: Text message with pre-filled content',
                '• **WiFi**: Wireless network connection details',
                '• **Facebook**: Profile links and sharing',
                '• **Twitter**: Profile links and tweet content'
              ]
            },
            {
              question: 'Is QRCodeForge really free?',
              answer: [
                'Yes, QRCodeForge is completely free to use with no hidden costs.',
                'You can generate unlimited QR codes without registration.',
                'All features including customization and history are free.',
                'We are supported by ethical advertising that doesn\'t interfere with functionality.'
              ]
            }
          ]
        },
        {
          title: 'QR Code Types Guide',
          icon: <Type className="w-5 h-5" />,
          content: [
            {
              question: 'URL/Website QR Codes',
              answer: [
                'Perfect for linking to websites, landing pages, or online content.',
                '• Enter the complete URL including http:// or https://',
                '• Works with any website, including social media profiles',
                '• Great for business cards, flyers, and marketing materials',
                '• Example: https://www.yourwebsite.com'
              ]
            },
            {
              question: 'WiFi QR Codes',
              answer: [
                'Allow users to connect to your WiFi network instantly.',
                '• Enter your network name (SSID) exactly as it appears',
                '• Select security type: WPA/WPA2, WEP, or None',
                '• Add password for secured networks',
                '• Check "Hidden Network" if your SSID is hidden',
                '• Perfect for restaurants, offices, and guest networks'
              ]
            },
            {
              question: 'Email QR Codes',
              answer: [
                'Pre-fill email composition with recipient, subject, and message.',
                '• Email address is required',
                '• Subject and message are optional',
                '• Users can still edit before sending',
                '• Great for contact information and support requests'
              ]
            },
            {
              question: 'Social Media QR Codes',
              answer: [
                '**Facebook**: Link to profiles, pages, or content to share',
                '• Profile format: https://www.facebook.com/username',
                '• Page format: https://www.facebook.com/pagename',
                '**Twitter**: Link to profiles or pre-filled tweets',
                '• Profile: Enter @username or full URL',
                '• Tweet: Enter text content (max 280 characters)'
              ]
            }
          ]
        },
        {
          title: 'Customization Options',
          icon: <Palette className="w-5 h-5" />,
          content: [
            {
              question: 'How do I customize colors?',
              answer: [
                'Click the "Customize" button to access color options.',
                '• **Foreground Color**: The QR code pattern (usually black)',
                '• **Background Color**: The background (usually white)',
                '• Use high contrast for better scanning',
                '• Avoid red/green combinations for accessibility',
                '• Test your QR code after color changes'
              ]
            },
            {
              question: 'What size should I choose?',
              answer: [
                'QR code size depends on where you\'ll use it:',
                '• **Business cards**: 256px minimum',
                '• **Flyers/posters**: 512px or larger',
                '• **Billboards**: 1024px or larger',
                '• **Digital displays**: Match your display resolution',
                'Rule of thumb: Scanning distance = 10x QR code width'
              ]
            },
            {
              question: 'Error correction levels explained',
              answer: [
                'Higher levels can recover from more damage but create denser codes:',
                '• **Low (~7%)**: Digital use, clean environments',
                '• **Medium (~15%)**: General purpose (recommended)',
                '• **Quartile (~25%)**: Industrial or outdoor use',
                '• **High (~30%)**: Maximum damage tolerance',
                'Choose based on expected wear and environment.'
              ]
            }
          ]
        },
        {
          title: 'Scanning & Compatibility',
          icon: <Smartphone className="w-5 h-5" />,
          content: [
            {
              question: 'How do users scan QR codes?',
              answer: [
                '**Modern smartphones** (iOS 11+, Android 8+):',
                '• Open the camera app',
                '• Point at the QR code',
                '• Tap the notification that appears',
                '**Older devices**: Download a QR scanner app from app store',
                '**Best practices**: Good lighting, steady hand, clean QR code'
              ]
            },
            {
              question: 'Are QR codes compatible worldwide?',
              answer: [
                'Yes! QR codes follow international standards (ISO/IEC 18004).',
                '• Work on all major platforms: iOS, Android, Windows',
                '• Compatible with all QR code readers globally',
                '• No special apps required on modern devices',
                '• Support for multiple languages and character sets'
              ]
            },
            {
              question: 'Why won\'t my QR code scan?',
              answer: [
                'Common issues and solutions:',
                '• **Too small**: Make it at least 2x2cm for close scanning',
                '• **Low contrast**: Use dark foreground on light background',
                '• **Damaged**: Ensure QR code isn\'t scratched or distorted',
                '• **Poor lighting**: Scan in good light conditions',
                '• **Wrong distance**: Try moving closer or further away',
                '• **Clean surface**: Remove dirt, glare, or reflections'
              ]
            }
          ]
        },
        {
          title: 'History & Management',
          icon: <History className="w-5 h-5" />,
          content: [
            {
              question: 'How does QR code history work?',
              answer: [
                'Your QR code history is automatically saved locally in your browser.',
                '• View all generated QR codes in the History tab',
                '• Re-download previous QR codes anytime',
                '• Regenerate QR codes with the same settings',
                '• Clear all history with one click if needed',
                'Note: History is device-specific and stored locally only.'
              ]
            },
            {
              question: 'Can I edit a saved QR code?',
              answer: [
                'QR codes themselves cannot be edited once generated, but you can:',
                '• Click "Regenerate" in history to load the settings',
                '• Modify the content or customization options',
                '• Generate a new QR code with updated information',
                '• The old QR code will remain unchanged'
              ]
            },
            {
              question: 'How long is history kept?',
              answer: [
                'History is stored in your browser\'s local storage:',
                '• Keeps up to 50 most recent QR codes automatically',
                '• Persists until you clear browser data or click "Clear All"',
                '• Not affected by closing the browser or restarting device',
                '• Private to your device - not shared or synced'
              ]
            }
          ]
        },
        {
          title: 'Privacy & Security',
          icon: <Shield className="w-5 h-5" />,
          content: [
            {
              question: 'How secure is my data?',
              answer: [
                'Your data is completely secure with QRCodeForge:',
                '• All QR generation happens in your browser (client-side)',
                '• We never see, store, or transmit your QR code content',
                '• No data is sent to our servers during generation',
                '• History is stored only on your device',
                '• No account registration required'
              ]
            },
            {
              question: 'What data do you collect?',
              answer: [
                'We collect minimal, anonymous data for improvement:',
                '• General usage statistics (page views, popular features)',
                '• Technical information (browser type, device type)',
                '• No personal information or QR code content',
                '• See our Privacy Policy for complete details'
              ]
            },
            {
              question: 'Can I use QRCodeForge offline?',
              answer: [
                'Partial offline functionality is available:',
                '• Once loaded, basic QR generation works offline',
                '• History and customization features work offline',
                '• Initial page load requires internet connection',
                '• Some advanced features may need connectivity'
              ]
            }
          ]
        },
        {
          title: 'Best Practices',
          icon: <Settings className="w-5 h-5" />,
          content: [
            {
              question: 'QR Code Design Best Practices',
              answer: [
                '• **Size**: Minimum 2x2cm for business cards, larger for distance viewing',
                '• **Contrast**: Dark foreground on light background for best scanning',
                '• **Quiet Zone**: Leave white space around the QR code',
                '• **Testing**: Always test with multiple devices before printing',
                '• **Error Correction**: Use Medium level for most applications',
                '• **Content**: Keep URLs short and test that they work'
              ]
            },
            {
              question: 'Marketing & Business Usage',
              answer: [
                '• **Call to Action**: Add text like "Scan to visit our website"',
                '• **Placement**: Eye level, well-lit, easily accessible locations',
                '• **Context**: Make it clear what users will get by scanning',
                '• **Mobile-Friendly**: Ensure destination content works on phones',
                '• **Analytics**: Use URL shorteners with tracking if needed',
                '• **Updates**: Use dynamic URLs for content that might change'
              ]
            },
            {
              question: 'Common Use Cases',
              answer: [
                '• **Business Cards**: Contact info, website, or vCard',
                '• **Restaurant Menus**: Link to digital menu or ordering',
                '• **Event Check-in**: Registration or information pages',
                '• **Product Info**: Manuals, specifications, or support',
                '• **WiFi Sharing**: Guest network access',
                '• **Social Media**: Profile links or content sharing',
                '• **Payment**: Link to payment pages or apps'
              ]
            }
          ]
        },
        {
          title: 'Troubleshooting',
          icon: <HelpCircle className="w-5 h-5" />,
          content: [
            {
              question: 'QR code won\'t generate',
              answer: [
                'Check these common issues:',
                '• **Required fields**: Ensure all required information is entered',
                '• **Valid format**: URLs must include http:// or https://',
                '• **Email format**: Check email address format is correct',
                '• **Text length**: Very long text might not generate',
                '• **Special characters**: Some characters might cause issues',
                '• **Browser**: Try refreshing the page or different browser'
              ]
            },
            {
              question: 'Poor scanning performance',
              answer: [
                'Improve scanning reliability:',
                '• **Increase size**: Make the QR code larger',
                '• **Higher contrast**: Use black on white colors',
                '• **Error correction**: Increase to Quartile or High level',
                '• **Clean printing**: Ensure high-quality, clean output',
                '• **Test multiple devices**: Verify with different phones',
                '• **Lighting**: Ensure good lighting when scanning'
              ]
            },
            {
              question: 'Browser or device issues',
              answer: [
                'If you experience technical problems:',
                '• **Clear cache**: Refresh page or clear browser cache',
                '• **Update browser**: Use latest version of your browser',
                '• **JavaScript**: Ensure JavaScript is enabled',
                '• **Different browser**: Try Chrome, Firefox, or Safari',
                '• **Mobile issues**: Try desktop version or different device',
                '• **Ad blockers**: Disable if preventing proper function'
              ]
            }
          ]
        }
      ]
    },
    zh: {
      title: '帮助文档',
      subtitle: '关于使用 QRCodeForge 您需要了解的一切',
      sections: [
        {
          title: '快速入门',
          icon: <QrCode className="w-5 h-5" />,
          content: [
            {
              question: '如何创建我的第一个二维码？',
              answer: [
                '1. 从左侧面板选择二维码类型（网址、文本、邮件等）',
                '2. 在表单中填写必需信息',
                '3. 如需要，自定义颜色和尺寸',
                '4. 二维码将自动生成',
                '5. 右键保存或使用下载选项'
              ]
            },
            {
              question: '我可以创建哪些类型的二维码？',
              answer: [
                '• **网址/网站**：链接到任何网站或网页',
                '• **纯文本**：最多 4,296 个字符的任何文本内容',
                '• **邮件**：预填收件人、主题和消息的邮件',
                '• **电话**：直接拨号电话号码',
                '• **短信**：带有预填内容的短信',
                '• **WiFi**：无线网络连接详情',
                '• **Facebook**：个人资料链接和分享',
                '• **Twitter**：个人资料链接和推文内容'
              ]
            },
            {
              question: 'QRCodeForge 真的免费吗？',
              answer: [
                '是的，QRCodeForge 完全免费使用，无隐藏费用。',
                '您可以生成无限数量的二维码，无需注册。',
                '所有功能包括自定义和历史记录都是免费的。',
                '我们通过不干扰功能的道德广告来支持运营。'
              ]
            }
          ]
        },
        {
          title: '二维码类型指南',
          icon: <Type className="w-5 h-5" />,
          content: [
            {
              question: '网址/网站二维码',
              answer: [
                '完美用于链接到网站、着陆页或在线内容。',
                '• 输入完整 URL，包括 http:// 或 https://',
                '• 适用于任何网站，包括社交媒体资料',
                '• 适合名片、传单和营销材料',
                '• 示例：https://www.yourwebsite.com'
              ]
            },
            {
              question: 'WiFi 二维码',
              answer: [
                '让用户能够瞬间连接到您的 WiFi 网络。',
                '• 准确输入网络名称（SSID），与显示的完全一致',
                '• 选择安全类型：WPA/WPA2、WEP 或无',
                '• 为安全网络添加密码',
                '• 如果 SSID 隐藏，请勾选"隐藏网络"',
                '• 适合餐厅、办公室和访客网络'
              ]
            },
            {
              question: '邮件二维码',
              answer: [
                '预填收件人、主题和消息的邮件编写。',
                '• 邮件地址是必需的',
                '• 主题和消息是可选的',
                '• 用户在发送前仍可编辑',
                '• 适合联系信息和支持请求'
              ]
            },
            {
              question: '社交媒体二维码',
              answer: [
                '**Facebook**：链接到个人资料、页面或要分享的内容',
                '• 个人资料格式：https://www.facebook.com/username',
                '• 页面格式：https://www.facebook.com/pagename',
                '**Twitter**：链接到个人资料或预填推文',
                '• 个人资料：输入 @用户名 或完整 URL',
                '• 推文：输入文本内容（最多 280 字符）'
              ]
            }
          ]
        },
        {
          title: '自定义选项',
          icon: <Palette className="w-5 h-5" />,
          content: [
            {
              question: '如何自定义颜色？',
              answer: [
                '点击"自定义"按钮访问颜色选项。',
                '• **前景色**：二维码图案（通常为黑色）',
                '• **背景色**：背景（通常为白色）',
                '• 使用高对比度以获得更好的扫描效果',
                '• 避免红/绿组合以提高可访问性',
                '• 更改颜色后测试您的二维码'
              ]
            },
            {
              question: '我应该选择什么尺寸？',
              answer: [
                '二维码尺寸取决于使用场所：',
                '• **名片**：最小 256px',
                '• **传单/海报**：512px 或更大',
                '• **广告牌**：1024px 或更大',
                '• **数字显示屏**：匹配您的显示分辨率',
                '经验法则：扫描距离 = 二维码宽度的 10 倍'
              ]
            },
            {
              question: '错误纠正级别解释',
              answer: [
                '更高级别能从更多损坏中恢复，但会创建更密集的代码：',
                '• **低（~7%）**：数字使用，清洁环境',
                '• **中（~15%）**：通用目的（推荐）',
                '• **高（~25%）**：工业或户外使用',
                '• **最高（~30%）**：最大损坏容忍度',
                '根据预期磨损和环境选择。'
              ]
            }
          ]
        },
        {
          title: '扫描和兼容性',
          icon: <Smartphone className="w-5 h-5" />,
          content: [
            {
              question: '用户如何扫描二维码？',
              answer: [
                '**现代智能手机**（iOS 11+，Android 8+）：',
                '• 打开相机应用',
                '• 对准二维码',
                '• 点击出现的通知',
                '**旧设备**：从应用商店下载二维码扫描应用',
                '**最佳实践**：良好光线、稳定手持、清洁二维码'
              ]
            },
            {
              question: '二维码在全球都兼容吗？',
              answer: [
                '是的！二维码遵循国际标准（ISO/IEC 18004）。',
                '• 适用于所有主要平台：iOS、Android、Windows',
                '• 与全球所有二维码阅读器兼容',
                '• 现代设备无需特殊应用',
                '• 支持多种语言和字符集'
              ]
            },
            {
              question: '为什么我的二维码扫描不了？',
              answer: [
                '常见问题及解决方案：',
                '• **太小**：近距离扫描至少要 2x2cm',
                '• **对比度低**：使用浅色背景上的深色前景',
                '• **损坏**：确保二维码没有刮伤或变形',
                '• **光线不足**：在良好光线条件下扫描',
                '• **距离不当**：尝试靠近或远离',
                '• **表面不洁**：清除污垢、眩光或反射'
              ]
            }
          ]
        },
        {
          title: '历史记录和管理',
          icon: <History className="w-5 h-5" />,
          content: [
            {
              question: '二维码历史记录如何工作？',
              answer: [
                '您的二维码历史记录会自动保存在浏览器本地。',
                '• 在历史记录选项卡中查看所有生成的二维码',
                '• 随时重新下载之前的二维码',
                '• 使用相同设置重新生成二维码',
                '• 如需要，一键清除所有历史记录',
                '注意：历史记录特定于设备且仅本地存储。'
              ]
            },
            {
              question: '我可以编辑已保存的二维码吗？',
              answer: [
                '二维码本身一旦生成就无法编辑，但您可以：',
                '• 在历史记录中点击"重新生成"加载设置',
                '• 修改内容或自定义选项',
                '• 使用更新的信息生成新二维码',
                '• 旧二维码将保持不变'
              ]
            },
            {
              question: '历史记录保存多长时间？',
              answer: [
                '历史记录存储在您浏览器的本地存储中：',
                '• 自动保留最近的 50 个二维码',
                '• 持续到您清除浏览器数据或点击"清除全部"',
                '• 不受关闭浏览器或重启设备影响',
                '• 您的设备私有 - 不共享或同步'
              ]
            }
          ]
        },
        {
          title: '隐私和安全',
          icon: <Shield className="w-5 h-5" />,
          content: [
            {
              question: '我的数据有多安全？',
              answer: [
                '您的数据在 QRCodeForge 中完全安全：',
                '• 所有二维码生成都在您的浏览器中进行（客户端）',
                '• 我们从不查看、存储或传输您的二维码内容',
                '• 生成过程中不向我们服务器发送数据',
                '• 历史记录仅存储在您的设备上',
                '• 无需注册账户'
              ]
            },
            {
              question: '你们收集什么数据？',
              answer: [
                '我们收集最少的匿名数据用于改进：',
                '• 一般使用统计（页面浏览、热门功能）',
                '• 技术信息（浏览器类型、设备类型）',
                '• 不收集个人信息或二维码内容',
                '• 完整详情请查看我们的隐私政策'
              ]
            },
            {
              question: '我可以离线使用 QRCodeForge 吗？',
              answer: [
                '提供部分离线功能：',
                '• 加载后，基本二维码生成可离线工作',
                '• 历史记录和自定义功能可离线使用',
                '• 初始页面加载需要网络连接',
                '• 一些高级功能可能需要网络连接'
              ]
            }
          ]
        },
        {
          title: '最佳实践',
          icon: <Settings className="w-5 h-5" />,
          content: [
            {
              question: '二维码设计最佳实践',
              answer: [
                '• **尺寸**：名片最小 2x2cm，远距离观看需要更大',
                '• **对比度**：浅色背景上的深色前景获得最佳扫描效果',
                '• **留白区域**：二维码周围留白色空间',
                '• **测试**：打印前务必用多台设备测试',
                '• **错误纠正**：大多数应用使用中等级别',
                '• **内容**：保持 URL 简短并测试其有效性'
              ]
            },
            {
              question: '营销和商业使用',
              answer: [
                '• **行动号召**：添加"扫描访问我们网站"等文本',
                '• **位置**：眼部水平、光线充足、易于访问的位置',
                '• **背景说明**：明确用户扫描后会得到什么',
                '• **移动友好**：确保目标内容在手机上正常工作',
                '• **分析**：如需要可使用带跟踪的 URL 缩短器',
                '• **更新**：对可能变化的内容使用动态 URL'
              ]
            },
            {
              question: '常见使用案例',
              answer: [
                '• **名片**：联系信息、网站或 vCard',
                '• **餐厅菜单**：链接到数字菜单或点餐',
                '• **活动签到**：注册或信息页面',
                '• **产品信息**：说明书、规格或支持',
                '• **WiFi 分享**：访客网络接入',
                '• **社交媒体**：个人资料链接或内容分享',
                '• **支付**：链接到支付页面或应用'
              ]
            }
          ]
        },
        {
          title: '故障排除',
          icon: <HelpCircle className="w-5 h-5" />,
          content: [
            {
              question: '二维码无法生成',
              answer: [
                '检查这些常见问题：',
                '• **必填字段**：确保输入所有必需信息',
                '• **有效格式**：URL 必须包含 http:// 或 https://',
                '• **邮件格式**：检查邮件地址格式是否正确',
                '• **文本长度**：过长文本可能无法生成',
                '• **特殊字符**：某些字符可能造成问题',
                '• **浏览器**：尝试刷新页面或不同浏览器'
              ]
            },
            {
              question: '扫描性能差',
              answer: [
                '提高扫描可靠性：',
                '• **增加尺寸**：使二维码更大',
                '• **更高对比度**：使用黑白色彩',
                '• **错误纠正**：增加到高或最高级别',
                '• **清洁打印**：确保高质量、清洁输出',
                '• **多设备测试**：用不同手机验证',
                '• **光线**：确保扫描时光线充足'
              ]
            },
            {
              question: '浏览器或设备问题',
              answer: [
                '如果遇到技术问题：',
                '• **清除缓存**：刷新页面或清除浏览器缓存',
                '• **更新浏览器**：使用浏览器最新版本',
                '• **JavaScript**：确保启用 JavaScript',
                '• **不同浏览器**：尝试 Chrome、Firefox 或 Safari',
                '• **移动问题**：尝试桌面版本或不同设备',
                '• **广告拦截器**：如阻碍正常功能请禁用'
              ]
            }
          ]
        }
      ]
    }
  };

  const currentContent = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SharedHeader />

      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Header */}
        <header className="pt-20 pb-8">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              {currentContent.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {currentContent.subtitle}
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <div className="space-y-6">
            {currentContent.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection(sectionIndex)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-white/10 rounded-lg mr-3 text-blue-300">
                      {section.icon}
                    </div>
                    <span className="text-xl font-semibold text-white">{section.title}</span>
                  </div>
                  {openSections.includes(sectionIndex) ? (
                    <ChevronUp className="w-5 h-5 text-white" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white" />
                  )}
                </button>

                {openSections.includes(sectionIndex) && (
                  <div className="px-6 pb-6">
                    <div className="space-y-6">
                      {section.content.map((item, itemIndex) => (
                        <div key={itemIndex} className="border-l-2 border-blue-400/30 pl-4">
                          <h3 className="text-lg font-medium text-white mb-3">
                            {item.question}
                          </h3>
                          <div className="space-y-2">
                            {item.answer.map((paragraph, pIndex) => (
                              <p
                                key={pIndex}
                                className="text-gray-300 leading-relaxed text-sm"
                                dangerouslySetInnerHTML={{
                                  __html: paragraph
                                    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
                                    .replace(/• /g, '• ')
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-12 bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'zh' ? '仍需要帮助？' : 'Still Need Help?'}
            </h2>
            <p className="text-gray-300 mb-4">
              {language === 'zh'
                ? '如果您有其他问题或建议，我们很乐意为您提供帮助。'
                : 'If you have additional questions or suggestions, we\'d love to help you.'
              }
            </p>
            <p className="text-blue-300">
              {language === 'zh'
                ? '邮箱：support@qrcodeforge.com'
                : 'Email: support@qrcodeforge.com'
              }
            </p>
            <p className="text-blue-300 mt-2">
              {language === 'zh'
                ? '网站：QRCodeForge.com'
                : 'Website: QRCodeForge.com'
              }
            </p>
          </div>
        </div>
        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default Help;